/**
 * Item 2 (Jun-2026, User-Wunsch) — Buchhaltung-Tab „Arbeitszeiten".
 * Einsehen + Korrigieren von Stempelungen & Abwesenheiten je Mitarbeiter,
 * Arbeitszeit-Regeln (Soll, Urlaub/Ausgleichskontingent) und Bundesland für
 * die automatische Feiertagsberechnung.
 */
import { useState, useEffect, useCallback } from "react";
import { Loader2, Plus, Trash2, Save, Settings as SettingsIcon, CalendarDays, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

function toLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}
function fromLocalInput(v) {
    return v ? new Date(v).toISOString() : null;
}
function secsToHM(s) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h ${String(m).padStart(2, "0")}m`;
}
const KINDS = { urlaub: "Urlaub", schule: "Berufsschule", ausgleich: "Ausgleichstag", krank: "Krank" };

export default function ArbeitszeitenTab() {
    const [employees, setEmployees] = useState([]);
    const [empId, setEmpId] = useState("");
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [showSettings, setShowSettings] = useState(false);
    const [settings, setSettings] = useState(null);
    const [bundesland, setBundesland] = useState("");
    // Jul-2026 (User-Wunsch): Stempel-Erinnerungen je Händler (Ein/Aus + Uhrzeiten).
    const [remindersEnabled, setRemindersEnabled] = useState(true);
    const [clockInReminder, setClockInReminder] = useState("09:30");
    const [clockOutReminder, setClockOutReminder] = useState("19:00");
    const [calcStartDate, setCalcStartDate] = useState("2026-08-01");
    const [blOptions, setBlOptions] = useState([]);
    const [newPunch, setNewPunch] = useState({ date: "", start: "", end: "" });
    const [newAbs, setNewAbs] = useState({ kind: "urlaub", date_from: "", date_to: "", note: "" });
    const [editPunch, setEditPunch] = useState({});

    useEffect(() => {
        Promise.all([
            api.get("/dealer/time/employees").catch(() => ({ data: { items: [] } })),
            api.get("/dealer/time/dealer-settings").catch(() => ({ data: {} })),
        ]).then(([e, ds]) => {
            const items = e.data?.items || [];
            setEmployees(items);
            if (items[0]) setEmpId(items[0].id);
            setBundesland(ds.data?.bundesland || "");
            setRemindersEnabled(ds.data?.reminders_enabled !== false);
            setClockInReminder(ds.data?.clock_in_reminder || "09:30");
            setClockOutReminder(ds.data?.clock_out_reminder || "19:00");
            setCalcStartDate(ds.data?.calc_start_date || "2026-08-01");
            setBlOptions(ds.data?.bundeslaender || []);
            setLoading(false);
        });
    }, []);

    const load = useCallback(async () => {
        if (!empId) return;
        setBusy("load");
        try {
            const r = await api.get(`/dealer/time/entries?employee_id=${empId}&month=${month}`);
            setData(r.data);
            setSettings(r.data?.summary?.settings || null);
        } catch {
            toast.error("Daten konnten nicht geladen werden");
        } finally {
            setBusy("");
        }
    }, [empId, month]);

    useEffect(() => { if (empId) load(); }, [empId, month, load]);

    const savePunchEdit = async (id) => {
        const e = editPunch[id];
        setBusy(id);
        try {
            await api.patch(`/dealer/time/entries/${id}`, {
                clock_in: fromLocalInput(e.clock_in),
                clock_out: e.clock_out ? fromLocalInput(e.clock_out) : null,
                note: e.note || "",
            });
            toast.success("Eintrag gespeichert");
            setEditPunch((p) => { const n = { ...p }; delete n[id]; return n; });
            await load();
        } catch { toast.error("Speichern fehlgeschlagen"); } finally { setBusy(""); }
    };

    const deletePunch = async (id) => {
        setBusy(id);
        try { await api.delete(`/dealer/time/entries/${id}`); await load(); }
        catch { toast.error("Löschen fehlgeschlagen"); } finally { setBusy(""); }
    };

    const addPunch = async () => {
        if (!newPunch.date || !newPunch.start) { toast.error("Datum & Beginn nötig"); return; }
        setBusy("add-punch");
        try {
            await api.post("/dealer/time/entries", {
                employee_id: empId,
                clock_in: fromLocalInput(`${newPunch.date}T${newPunch.start}`),
                clock_out: newPunch.end ? fromLocalInput(`${newPunch.date}T${newPunch.end}`) : null,
            });
            setNewPunch({ date: "", start: "", end: "" });
            await load();
        } catch { toast.error("Anlegen fehlgeschlagen"); } finally { setBusy(""); }
    };

    const addAbsence = async () => {
        if (!newAbs.date_from) { toast.error("Startdatum nötig"); return; }
        setBusy("add-abs");
        try {
            await api.post("/dealer/time/absences", { employee_id: empId, ...newAbs });
            setNewAbs({ kind: "urlaub", date_from: "", date_to: "", note: "" });
            await load();
        } catch { toast.error("Anlegen fehlgeschlagen"); } finally { setBusy(""); }
    };

    const setAbsStatus = async (id, status) => {
        setBusy(id);
        try { await api.patch(`/dealer/time/absences/${id}`, { status }); await load(); }
        catch { toast.error("Fehlgeschlagen"); } finally { setBusy(""); }
    };
    const deleteAbs = async (id) => {
        setBusy(id);
        try { await api.delete(`/dealer/time/absences/${id}`); await load(); }
        catch { toast.error("Löschen fehlgeschlagen"); } finally { setBusy(""); }
    };

    const saveSettings = async () => {
        setBusy("settings");
        try {
            await api.put(`/dealer/time/settings/${empId}`, { settings });
            await api.put("/dealer/time/dealer-settings", {
                bundesland,
                reminders_enabled: remindersEnabled,
                clock_in_reminder: clockInReminder,
                clock_out_reminder: clockOutReminder,
                calc_start_date: calcStartDate,
            });
            toast.success("Regeln gespeichert");
            setShowSettings(false);
            await load();
        } catch { toast.error("Speichern fehlgeschlagen"); } finally { setBusy(""); }
    };

    if (loading) return <div className="py-12 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Lädt …</div>;
    const summary = data?.summary;
    const punches = data?.punches || [];
    const absences = data?.absences || [];
    const holidays = data?.holidays || [];

    return (
        <div data-testid="arbeitszeiten-tab">
            {/* Steuerung */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                    <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">Mitarbeiter</label>
                    <select value={empId} onChange={(e) => setEmpId(e.target.value)} className="h-9 text-sm border border-gray-300 rounded-sm px-2 bg-white min-w-[200px]" data-testid="arbeitszeiten-employee-select" aria-label="Mitarbeiter wählen">
                        {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">Monat</label>
                    <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 text-sm w-40" data-testid="arbeitszeiten-month" />
                </div>
                <Button variant="outline" onClick={() => setShowSettings((s) => !s)} className="h-9 text-xs" data-testid="arbeitszeiten-settings-toggle">
                    <SettingsIcon className="w-4 h-4 mr-1.5" /> Regeln & Bundesland
                </Button>
                <Button
                    variant="outline"
                    onClick={async () => {
                        try {
                            const r = await api.get(`/dealer/time/report.pdf?month=${month}`, { responseType: "blob" });
                            window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
                        } catch { toast.error("Monatsbericht konnte nicht erstellt werden"); }
                    }}
                    className="h-9 text-xs"
                    data-testid="arbeitszeiten-report-btn"
                >
                    <Printer className="w-4 h-4 mr-1.5" /> Monatsbericht (PDF)
                </Button>
            </div>

            {showSettings && settings && (
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="arbeitszeiten-settings">
                    <SetNum label="Wochenstunden" val={settings.weekly_hours} on={(v) => setSettings((s) => ({ ...s, weekly_hours: v }))} t="set-weekly" />
                    <SetNum label="Soll Std/Tag" val={settings.daily_target_hours} on={(v) => setSettings((s) => ({ ...s, daily_target_hours: v }))} t="set-daily" />
                    <SetNum label="Pause (Min)" val={settings.break_minutes} on={(v) => setSettings((s) => ({ ...s, break_minutes: v }))} t="set-break" />
                    <SetNum label="Urlaubstage/Jahr" val={settings.vacation_days_per_year} on={(v) => setSettings((s) => ({ ...s, vacation_days_per_year: v }))} t="set-vacation" />
                    <SetNum label="Ausgleichstage/Jahr" val={settings.ausgleich_days_per_year} on={(v) => setSettings((s) => ({ ...s, ausgleich_days_per_year: v }))} t="set-ausgleich" />
                    <div>
                        <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">Bundesland (Feiertage)</label>
                        <select value={bundesland} onChange={(e) => setBundesland(e.target.value)} className="h-9 text-sm border border-gray-300 rounded-sm px-2 bg-white w-full" data-testid="arbeitszeiten-bundesland" aria-label="Bundesland wählen">
                            <option value="">– bundesweit –</option>
                            {blOptions.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">Berechnungs-Stichtag</label>
                        <input type="date" value={calcStartDate} onChange={(e) => setCalcStartDate(e.target.value)}
                            className="h-9 text-sm border border-gray-300 rounded-sm px-2 bg-white w-full" data-testid="arbeitszeiten-calc-start" />
                        <p className="text-[10px] text-gray-400 mt-0.5">Soll-/Überstunden werden erst ab diesem Datum berechnet.</p>
                    </div>
                    <div className="col-span-2 md:col-span-4 border-t border-gray-200 pt-2 flex flex-wrap items-end gap-3" data-testid="arbeitszeiten-reminder-settings">
                        <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
                            <input type="checkbox" checked={remindersEnabled} onChange={(e) => setRemindersEnabled(e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="arbeitszeiten-reminders-toggle" />
                            Stempel-Erinnerungen per E-Mail (Ein-/Ausstempeln)
                        </label>
                        <div>
                            <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">Erinnerung Einstempeln ab</label>
                            <input type="time" value={clockInReminder} onChange={(e) => setClockInReminder(e.target.value)} disabled={!remindersEnabled}
                                className="h-9 text-sm border border-gray-300 rounded-sm px-2 bg-white disabled:opacity-50" data-testid="arbeitszeiten-clockin-time" />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">Erinnerung Ausstempeln ab</label>
                            <input type="time" value={clockOutReminder} onChange={(e) => setClockOutReminder(e.target.value)} disabled={!remindersEnabled}
                                className="h-9 text-sm border border-gray-300 rounded-sm px-2 bg-white disabled:opacity-50" data-testid="arbeitszeiten-clockout-time" />
                        </div>
                        <p className="text-[10px] text-gray-400 basis-full">Erinnert eingeplante Mitarbeiter automatisch — außer an Wochenenden, Feiertagen (Bundesland) und bei genehmigter Abwesenheit (Urlaub/Krankheit). Max. 1 Erinnerung pro Tag & Art.</p>
                    </div>
                    <div className="col-span-2 md:col-span-4">
                        <Button size="sm" onClick={saveSettings} disabled={busy === "settings"} className="bg-[#111827] text-white" data-testid="arbeitszeiten-settings-save">
                            <Save className="w-4 h-4 mr-1.5" /> Speichern
                        </Button>
                    </div>
                </div>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Kpi label="Monatsstunden" value={secsToHM(data?.total_seconds || 0)} t="kpi-month-hours" />
                <Kpi label="Resturlaub" value={`${summary?.vacation_remaining ?? 0} / ${summary?.vacation_total ?? 0}`} cls="text-indigo-700" t="kpi-vacation" />
                <Kpi label="Ausgleich übrig" value={`${summary?.ausgleich_remaining ?? 0} / ${summary?.ausgleich_total ?? 0}`} t="kpi-ausgleich" />
                <Kpi label="Schultage" value={summary?.schule_days ?? 0} t="kpi-schule" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Stempelungen */}
                <div className="border border-gray-200 rounded-sm p-3">
                    <h3 className="font-bold text-sm mb-2">Stempelungen ({month})</h3>
                    <table className="w-full text-xs mb-2" data-testid="arbeitszeiten-punches">
                        <thead><tr className="text-left text-[10px] uppercase text-gray-500"><th className="py-1">Kommen</th><th>Gehen</th><th className="text-right">Dauer</th><th></th></tr></thead>
                        <tbody>
                            {punches.length === 0 && <tr><td colSpan="4" className="py-3 text-gray-400 text-center">Keine Stempelungen.</td></tr>}
                            {punches.map((p) => {
                                const ed = editPunch[p.id];
                                return (
                                    <tr key={p.id} className="border-t border-gray-100" data-testid={`punch-row-${p.id}`}>
                                        {ed ? (
                                            <>
                                                <td className="py-1"><input type="datetime-local" value={ed.clock_in} onChange={(e) => setEditPunch((x) => ({ ...x, [p.id]: { ...ed, clock_in: e.target.value } }))} className="text-xs border rounded px-1" /></td>
                                                <td><input type="datetime-local" value={ed.clock_out} onChange={(e) => setEditPunch((x) => ({ ...x, [p.id]: { ...ed, clock_out: e.target.value } }))} className="text-xs border rounded px-1" /></td>
                                                <td colSpan="2" className="text-right">
                                                    <Button size="sm" className="h-6 text-[10px] bg-emerald-600 text-white" onClick={() => savePunchEdit(p.id)} data-testid={`punch-save-${p.id}`}>OK</Button>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="py-1">{new Date(p.clock_in).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                                                <td>{p.clock_out ? new Date(p.clock_out).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : <span className="text-emerald-600">offen</span>}</td>
                                                <td className="text-right font-mono-num">{secsToHM(p.seconds || 0)}</td>
                                                <td className="text-right whitespace-nowrap">
                                                    <button onClick={() => setEditPunch((x) => ({ ...x, [p.id]: { clock_in: toLocalInput(p.clock_in), clock_out: toLocalInput(p.clock_out), note: p.note || "" } }))} className="text-indigo-600 text-[10px] mr-1.5" data-testid={`punch-edit-${p.id}`}>bearb.</button>
                                                    <button onClick={() => deletePunch(p.id)} className="text-red-500" data-testid={`punch-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5 inline" /></button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="flex items-end gap-1.5 border-t border-gray-100 pt-2">
                        <Input type="date" value={newPunch.date} onChange={(e) => setNewPunch((p) => ({ ...p, date: e.target.value }))} className="h-8 text-xs" data-testid="new-punch-date" />
                        <Input type="time" value={newPunch.start} onChange={(e) => setNewPunch((p) => ({ ...p, start: e.target.value }))} className="h-8 text-xs w-24" data-testid="new-punch-start" />
                        <Input type="time" value={newPunch.end} onChange={(e) => setNewPunch((p) => ({ ...p, end: e.target.value }))} className="h-8 text-xs w-24" data-testid="new-punch-end" />
                        <Button size="sm" onClick={addPunch} disabled={busy === "add-punch"} className="h-8 text-xs bg-[#111827] text-white" data-testid="new-punch-add"><Plus className="w-3.5 h-3.5" /></Button>
                    </div>
                </div>

                {/* Abwesenheiten + Feiertage */}
                <div className="border border-gray-200 rounded-sm p-3">
                    <h3 className="font-bold text-sm mb-2">Abwesenheiten</h3>
                    <div className="space-y-1 mb-2" data-testid="arbeitszeiten-absences">
                        {absences.length === 0 && <div className="text-xs text-gray-400 py-2">Keine.</div>}
                        {absences.map((a) => (
                            <div key={a.id} className="flex items-center justify-between text-xs py-1 border-b border-gray-50" data-testid={`abs-row-${a.id}`}>
                                <span>{KINDS[a.kind] || a.kind} <span className="text-gray-400">({a.days}T)</span> · {a.date_from === a.date_to ? a.date_from : `${a.date_from}–${a.date_to}`}</span>
                                <span className="flex items-center gap-1">
                                    {a.status !== "approved" && <button onClick={() => setAbsStatus(a.id, "approved")} className="text-emerald-600 text-[10px]" data-testid={`abs-approve-${a.id}`}>OK</button>}
                                    {a.status === "approved" && <span className="text-emerald-600 text-[9px] font-bold">genehmigt</span>}
                                    <button onClick={() => deleteAbs(a.id)} className="text-red-500" data-testid={`abs-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="flex flex-wrap items-end gap-1.5 border-t border-gray-100 pt-2 mb-3">
                        <select value={newAbs.kind} onChange={(e) => setNewAbs((p) => ({ ...p, kind: e.target.value }))} className="h-8 text-xs border rounded-sm px-1.5 bg-white" data-testid="new-abs-kind" aria-label="Abwesenheitsart wählen">
                            {Object.entries(KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        <Input type="date" value={newAbs.date_from} onChange={(e) => setNewAbs((p) => ({ ...p, date_from: e.target.value }))} className="h-8 text-xs" data-testid="new-abs-from" />
                        <Input type="date" value={newAbs.date_to} onChange={(e) => setNewAbs((p) => ({ ...p, date_to: e.target.value }))} className="h-8 text-xs" data-testid="new-abs-to" />
                        <Button size="sm" onClick={addAbsence} disabled={busy === "add-abs"} className="h-8 text-xs bg-[#111827] text-white" data-testid="new-abs-add"><Plus className="w-3.5 h-3.5" /></Button>
                    </div>
                    <h4 className="font-bold text-xs mb-1 flex items-center gap-1.5 text-gray-600"><CalendarDays className="w-3.5 h-3.5" /> Feiertage {month} {data?.bundesland && `(${data.bundesland})`}</h4>
                    <div className="space-y-0.5" data-testid="arbeitszeiten-holidays">
                        {holidays.length === 0 ? <div className="text-[11px] text-gray-400">Keine im Monat.</div> : holidays.map((h) => (
                            <div key={h.date} className="text-[11px] text-gray-600 flex justify-between"><span>{h.name}</span><span className="text-gray-400">{h.date}</span></div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Kpi({ label, value, cls = "text-[#111827]", t }) {
    return (
        <div className="bg-white border border-gray-200 rounded-sm px-4 py-3" data-testid={t}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</div>
            <div className={`text-xl font-black font-mono-num ${cls}`}>{value}</div>
        </div>
    );
}
function SetNum({ label, val, on, t }) {
    return (
        <div>
            <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">{label}</label>
            <Input type="number" step="0.5" value={val} onChange={(e) => on(Number(e.target.value))} className="h-9 text-sm" data-testid={t} />
        </div>
    );
}
